/**
 * AutoCancellationSystem.js
 *
 * Handles automatic flight cancellations due to prolonged weather holds,
 * excessive delays, or inability to find diversion airports.
 */

// Thresholds for automatic cancellation
export const CANCELLATION_THRESHOLDS = {
  WEATHER_GROUND_STOP_HOURS: 4,     // Cancel if grounded > 4 hours due to weather
  MAX_TOTAL_DELAY_MINUTES: 360,     // Cancel if total delay exceeds 6 hours
  CREW_LEGALITY_TIMEOUT_HOURS: 2,   // Cancel if crew goes illegal with no replacement
  NO_DIVERSION_AVAILABLE: true,     // Cancel if can't divert due to no suitable alternate
  FUEL_EMERGENCY_NO_ALTERNATE: true, // Cancel if fuel emergency with no safe diversion
};

// Cancellation cost factors
export const CANCELLATION_COSTS = {
  REFUND_PERCENT: 1.0,              // 100% refund to passengers
  REBOOKING_COST_PER_PAX: 100,      // Cost to rebook each passenger
  COMPENSATION_PER_PAX: 200,        // EU-style compensation per passenger
  AIRCRAFT_REPOSITIONING: 5000,     // Cost to reposition empty aircraft
  CREW_DEADHEAD: 2000,              // Cost to deadhead crew to next assignment
};

/**
 * Check if a flight should be automatically cancelled
 *
 * @param {Object} flight - Flight to check
 * @param {Date} gameTime - Current game time
 * @returns {Object} { shouldCancel: boolean, reason: string, type: string }
 */
export function checkAutoCancellation(flight, gameTime) {
  const now = new Date(gameTime);

  // Check weather ground stop duration
  if (flight.weatherHold && flight.weatherHoldStarted) {
    const holdStart = new Date(flight.weatherHoldStarted);
    const holdHours = (now - holdStart) / (1000 * 60 * 60);

    if (holdHours >= CANCELLATION_THRESHOLDS.WEATHER_GROUND_STOP_HOURS) {
      return {
        shouldCancel: true,
        reason: `Weather ground stop exceeded ${CANCELLATION_THRESHOLDS.WEATHER_GROUND_STOP_HOURS} hours`,
        type: 'weather_prolonged',
      };
    }
  }

  // Check total delay
  const totalDelay = flight.totalDelayMinutes || 0;
  if (totalDelay >= CANCELLATION_THRESHOLDS.MAX_TOTAL_DELAY_MINUTES) {
    return {
      shouldCancel: true,
      reason: `Total delay of ${totalDelay} minutes exceeds maximum of ${CANCELLATION_THRESHOLDS.MAX_TOTAL_DELAY_MINUTES}`,
      type: 'excessive_delay',
    };
  }

  // Check crew legality
  if (flight.crewIllegal && !flight.replacementCrewAssigned) {
    const illegalStart = flight.crewIllegalAt ? new Date(flight.crewIllegalAt) : null;
    if (illegalStart) {
      const illegalHours = (now - illegalStart) / (1000 * 60 * 60);
      if (illegalHours >= CANCELLATION_THRESHOLDS.CREW_LEGALITY_TIMEOUT_HOURS) {
        return {
          shouldCancel: true,
          reason: `Crew legality issue unresolved for ${Math.round(illegalHours)} hours`,
          type: 'crew_illegal',
        };
      }
    }
  }

  // Check fuel emergency with no diversion available
  if (flight.fuelEmergency && flight.noDiversionAvailable) {
    return {
      shouldCancel: true,
      reason: 'Fuel emergency with no safe diversion airport available',
      type: 'fuel_no_diversion',
    };
  }

  return {
    shouldCancel: false,
    reason: 'Flight OK',
    type: null,
  };
}

/**
 * Calculate costs associated with a cancelled flight
 *
 * @param {Object} flight - The cancelled flight
 * @param {string} cancellationType - Type of cancellation
 * @returns {Object} Cost breakdown
 */
export function calculateCancellationCosts(flight, cancellationType) {
  const passengers = flight.passengers ||
    flight.preCalculatedEarnings?.passengers || 100;
  const ticketRevenue = flight.preCalculatedEarnings?.revenue ||
    flight.revenue || (passengers * 250);

  // Full refund to passengers
  const refundCost = Math.round(ticketRevenue * CANCELLATION_COSTS.REFUND_PERCENT);

  // Rebooking costs (for passengers who want to rebook)
  const rebookingRate = 0.7; // 70% will rebook
  const rebookingCost = Math.round(passengers * rebookingRate * CANCELLATION_COSTS.REBOOKING_COST_PER_PAX);

  // Compensation (weather-related cancellations may have reduced compensation)
  const compensationRate = cancellationType === 'weather_prolonged' ? 0.5 : 1.0;
  const compensationCost = Math.round(passengers * CANCELLATION_COSTS.COMPENSATION_PER_PAX * compensationRate);

  // Aircraft repositioning (if not at hub)
  const repositioningCost = flight.origin !== flight.hubAirport ?
    CANCELLATION_COSTS.AIRCRAFT_REPOSITIONING : 0;

  // Crew costs
  const crewCost = CANCELLATION_COSTS.CREW_DEADHEAD;

  const totalCost = refundCost + rebookingCost + compensationCost + repositioningCost + crewCost;

  // Net loss (including lost revenue)
  const lostRevenue = ticketRevenue;
  const netLoss = totalCost + lostRevenue;

  return {
    refundCost,
    rebookingCost,
    compensationCost,
    repositioningCost,
    crewCost,
    totalCost,
    lostRevenue,
    netLoss,
    breakdown: [
      { name: 'Passenger Refunds', amount: refundCost },
      { name: 'Rebooking Costs', amount: rebookingCost },
      { name: 'Passenger Compensation', amount: compensationCost },
      { name: 'Aircraft Repositioning', amount: repositioningCost },
      { name: 'Crew Deadhead', amount: crewCost },
      { name: 'Lost Revenue', amount: lostRevenue },
    ],
  };
}

/**
 * Create a cancellation record for a flight
 *
 * @param {Object} flight - Original flight
 * @param {string} reason - Reason for cancellation
 * @param {string} type - Type of cancellation
 * @param {Object} costs - Cost breakdown
 * @returns {Object} Cancellation record
 */
export function createCancellationRecord(flight, reason, type, costs) {
  return {
    flightId: flight.id,
    flightNumber: flight.flightNumber,
    route: `${flight.origin}-${flight.destination}`,
    cancelledAt: new Date().toISOString(),
    reason,
    type,
    costs,
    passengers: flight.passengers || flight.preCalculatedEarnings?.passengers || 100,
    wasAirborne: ['takeoff', 'climb', 'cruise', 'descent', 'approach'].includes(flight.phase),
  };
}

/**
 * Process an automatic cancellation
 *
 * @param {Object} flight - Flight to cancel
 * @param {string} reason - Reason for cancellation
 * @param {string} type - Type of cancellation
 * @returns {Object} { cancelledFlight: Object, record: Object, costs: Object }
 */
export function processAutoCancellation(flight, reason, type) {
  const costs = calculateCancellationCosts(flight, type);
  const record = createCancellationRecord(flight, reason, type, costs);

  const cancelledFlight = {
    ...flight,
    status: 'cancelled',
    phase: 'cancelled',
    cancellationReason: reason,
    cancellationType: type,
    cancellationRecord: record,
    cancellationCost: costs.netLoss,
  };

  return {
    cancelledFlight,
    record,
    costs,
  };
}

/**
 * Get human-readable cancellation message for UI
 *
 * @param {string} type - Cancellation type
 * @param {string} reason - Detailed reason
 * @returns {Object} { title: string, message: string, severity: string }
 */
export function getCancellationMessage(type, reason) {
  const messages = {
    weather_prolonged: {
      title: 'Weather Cancellation',
      message: 'Flight cancelled due to extended weather ground stop. Passengers are being rebooked.',
      severity: 'warning',
    },
    excessive_delay: {
      title: 'Delay Cancellation',
      message: 'Flight cancelled due to excessive delays. Full refunds being processed.',
      severity: 'error',
    },
    crew_illegal: {
      title: 'Crew Cancellation',
      message: 'Flight cancelled due to crew duty time limitations. No replacement crew available.',
      severity: 'error',
    },
    fuel_no_diversion: {
      title: 'Safety Cancellation',
      message: 'Flight cancelled for safety reasons. No suitable alternate airport available.',
      severity: 'critical',
    },
  };

  return messages[type] || {
    title: 'Flight Cancelled',
    message: reason,
    severity: 'warning',
  };
}

/**
 * Check if cancellation affects EOS (Elite Operator Status)
 *
 * @param {string} cancellationType - Type of cancellation
 * @returns {Object} { affectsEOS: boolean, severity: string }
 */
export function getEOSImpact(cancellationType) {
  // Weather cancellations are partially excused
  if (cancellationType === 'weather_prolonged') {
    return {
      affectsEOS: true,
      severity: 'partial', // Counts as 0.5 cancellations for EOS
      eosWeight: 0.5,
    };
  }

  // Other cancellations fully count against EOS
  return {
    affectsEOS: true,
    severity: 'full',
    eosWeight: 1.0,
  };
}

export default {
  CANCELLATION_THRESHOLDS,
  CANCELLATION_COSTS,
  checkAutoCancellation,
  calculateCancellationCosts,
  createCancellationRecord,
  processAutoCancellation,
  getCancellationMessage,
  getEOSImpact,
};
